import { TAGS } from "../constants";
import {
  CreateDraftPrescription,
  CreatePrescriptionArgs,
  Prescription,
  PrescriptionMedicationCreate,
  PrescriptionMedicationInstruction,
  PrescriptionWithMedication,
  UpdatePrescriptionArgs,
  UpdatePrescriptionInstructions,
} from "../types";
import { api } from "./api";

export const prescriptionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * prescriptions
     */
    getPrescriptions: builder.query<
      Prescription[],
      {
        status?: string;
      }
    >({
      query: (params) => ({
        method: "GET",
        url: `/api/prescriptions`,
        params,
      }),
      providesTags: [TAGS.PRESCRIPTIONS],
    }),

    getPrescription: builder.query<Prescription, number>({
      query: (id) => ({
        url: `/api/prescriptions/${id}`,
        method: "GET",
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.PRESCRIPTION, id: args },
      ],
    }),

    updatePrescription: builder.mutation<Prescription, UpdatePrescriptionArgs>({
      query: ({ id, ...body }) => ({
        url: `/api/prescriptions/${id}`,
        method: "PUT",
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        dispatch(
          prescriptionsApi.util.updateQueryData(
            "getPrescription",
            data.id,
            () => data,
          ),
        );
      },
      invalidatesTags(_result, _error, arg) {
        if (arg.status === "denied") {
          return [{ type: TAGS.PATIENT, id: arg.patient_id }];
        }

        return [];
      },
    }),

    createPrescription: builder.mutation<Prescription, CreatePrescriptionArgs>({
      query: (body) => ({
        url: `/api/prescriptions`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_returns, _error, args) => [
        {
          type: TAGS.TASKS,
          id: args.patient_id,
        },
      ],
    }),

    getPrescriptionDetails: builder.query<
      PrescriptionMedicationInstruction,
      number
    >({
      query: (id) => ({
        url: `/api/prescriptions/${id}/details`,
        method: "GET",
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.PRESCRIPTION_DETAILS, id: args },
      ],
    }),

    updatePrescriptionInstructions: builder.mutation<
      void,
      {
        prescriptionId: number;
        body: { instructions: UpdatePrescriptionInstructions };
      }
    >({
      query: ({ prescriptionId, body }) => ({
        url: `/api/prescriptions/${prescriptionId}/instructions`,
        method: "PUT",
        body,
      }),
      invalidatesTags(_result, _error, args) {
        return [{ type: TAGS.PRESCRIPTION_DETAILS, id: args.prescriptionId }];
      },
    }),
    createDraftPrescription: builder.mutation<
      Prescription,
      CreateDraftPrescription
    >({
      query: (body) => ({
        method: "POST",
        url: `/api/prescriptions/draft`,
        body,
      }),
      invalidatesTags: (_returns, _error, args) => [
        {
          type: TAGS.PRESCRIPTION,
          id: args.patient_id,
        },
      ],
    }),
    getPatientDraftPrescriptions: builder.query<
      PrescriptionWithMedication[],
      number
    >({
      query: (patientId) => ({
        url: `/api/prescriptions/draft/patient/${patientId}`,
        method: "GET",
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.PRESCRIPTION, id: args },
      ],
    }),
    updatePrescriptionMedications: builder.mutation<
      Prescription,
      PrescriptionMedicationCreate
    >({
      query: (body) => ({
        method: "POST",
        url: `/api/prescription-medications/${body.prescription_id}/dangerous-update`,
        body,
      }),
      invalidatesTags: [TAGS.PRESCRIPTIONS],
    }),
  }),
});

export const {
  useGetPrescriptionQuery,
  useUpdatePrescriptionMutation,
  useCreatePrescriptionMutation,
  useGetPrescriptionDetailsQuery,
  useUpdatePrescriptionInstructionsMutation,
  useCreateDraftPrescriptionMutation,
  useGetPatientDraftPrescriptionsQuery,
  useGetPrescriptionsQuery,
  useUpdatePrescriptionMedicationsMutation,
} = prescriptionsApi;
